/**
 * useTextRangeRects — resolve a `text_range` selector against the host's
 * flattened text and return the painted rect array.
 *
 * Three call sites used to inline this same effect:
 *   - `TextHighlightAnnotationView` (paints persisted text_highlights)
 *   - `CommentTextRangeView`        (paints comment-with-text_range)
 *   - the live selection overlay    (in `use-block-detail-selection`)
 *
 * Each kept its own copy of the same ~60-line useEffect that:
 *   - waits a frame for layout to settle
 *   - reads the AnnotationViewContainer's rect as the rect origin
 *   - resolves the text host (panel in expanded mode, .block-view in
 *     regular mode)
 *   - builds a DOM Range from `(start, end)` offsets
 *   - shifts viewport rects into overlay-local coords
 *   - re-runs on scroll / resize / text-host ResizeObserver
 *
 * Hoisting it here keeps the three call sites in lockstep — a fix to
 * the rect math (e.g. accounting for a new ancestor scroll context)
 * lands once instead of three times.
 */
import { useEffect, useState } from "react";
import { buildRangeFromOffsets, findTextHost, getHostRect, getRangeClientRectsRelativeTo, } from "./text-offsets";
export function useTextRangeRects({ containerRef, mode, start, end, }) {
    const [rects, setRects] = useState([]);
    useEffect(() => {
        const overlay = containerRef.current;
        if (!overlay)
            return;
        if (start === undefined || end === undefined) {
            setRects([]);
            return;
        }
        function recompute() {
            if (start === undefined || end === undefined) {
                setRects([]);
                return;
            }
            const liveOverlay = containerRef.current;
            if (!liveOverlay)
                return;
            // Origin = container's own rect, so the rects render in the
            // right place inside the overlay layer.
            const originRect = getHostRect(liveOverlay);
            if (!originRect) {
                setRects([]);
                return;
            }
            // Text offsets resolve against the *content* host (panel in
            // expanded mode, `.block-view` in regular mode) — the overlay
            // itself has no text to walk.
            const textHost = findTextHost(liveOverlay, mode);
            if (!textHost) {
                setRects([]);
                return;
            }
            const liveRange = buildRangeFromOffsets(textHost, start, end);
            if (!liveRange) {
                setRects([]);
                return;
            }
            setRects(getRangeClientRectsRelativeTo(liveRange, originRect));
        }
        // Defer the first measurement to the next frame so the host has
        // committed its layout (the modal's FLIP grow / a stream card
        // mounting both produce mid-animation layouts that would lock in
        // transient rects).
        const raf = requestAnimationFrame(recompute);
        window.addEventListener("scroll", recompute, true);
        window.addEventListener("resize", recompute);
        // Re-measure every time the text host changes size. Critical for
        // the modal's open/close animation: the panel transitions its
        // `width`/`height` from the source-card rect up to ~70vw/70vh
        // (and back), which reflows the BlockView text but fires no
        // scroll or resize events. Without a ResizeObserver the rects
        // stay locked at the size measured during mount — visually a
        // tiny stream-card-sized highlight stranded in the expanded
        // panel.
        //
        // Observing the text host (panel / `.block-view`) also covers
        // font-size or content-density changes that don't bubble through
        // window-level events.
        const textHost = findTextHost(overlay, mode);
        let observer;
        if (textHost && typeof ResizeObserver !== "undefined") {
            observer = new ResizeObserver(() => recompute());
            observer.observe(textHost);
            // Also observe the overlay itself — when the panel grows, the
            // container's `inset: 0` tracks the panel's new size, and our
            // origin rect changes alongside the text.
            observer.observe(overlay);
        }
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("scroll", recompute, true);
            window.removeEventListener("resize", recompute);
            observer?.disconnect();
        };
    }, [containerRef, mode, start, end]);
    return rects;
}
